import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import type { RolloutDayKey } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../../../common/types';
import { planDayDate, planDayTitle } from '../rolloutDayCopy';
import { CanvasMilestone, ReleaseDayMilestone } from './CanvasMilestone';
import { LyricVideoMilestone } from './LyricVideoMilestone';
import { useRollout } from './RolloutContext';
import type { PlanStepView, StepPhase } from './rolloutModel';
import { rolloutMessages } from './rolloutMessages';
import { StepCard } from './StepCard';
import { useMilestoneFacts } from './useMilestoneFacts';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

type Row =
  | { kind: 'phase'; phase: StepPhase }
  | { kind: 'step'; step: PlanStepView }
  | { kind: 'today' }
  | { kind: 'releaseDay' }
  | { kind: 'lyricVideo' }
  | { kind: 'canvas' }
  | { kind: 'skipped'; step: PlanStepView };

/** The rows of the run sheet in order: phases, steps, the today line, the milestones, the skipped lines. */
function rowsOf(steps: PlanStepView[], today: string): Row[] {
  const rows: Row[] = [];
  let todayPlaced = false;
  const placeToday = (date: string) => {
    if (!todayPlaced && date >= today && steps.some((step) => step.date < today)) {
      rows.push({ kind: 'today' });
      todayPlaced = true;
    }
  };
  (['build', 'release', 'after'] as StepPhase[]).forEach((phase) => {
    const own = steps.filter((step) => step.phase === phase);
    rows.push({ kind: 'phase', phase });
    if (phase === 'release') {
      rows.push({ kind: 'releaseDay' }, { kind: 'lyricVideo' });
    }
    if (phase === 'after') {
      rows.push({ kind: 'canvas' });
    }
    own
      .filter((step) => step.day.skipped !== true)
      .forEach((step) => {
        placeToday(step.date);
        rows.push({ kind: 'step', step });
      });
    own.filter((step) => step.day.skipped === true).forEach((step) => rows.push({ kind: 'skipped', step }));
  });
  return rows;
}

const PHASE_TITLE = {
  build: rolloutMessages.phaseBuild,
  release: rolloutMessages.phaseRelease,
  after: rolloutMessages.phaseAfter,
} as const;

/** The vertical run sheet (design rollout-plan a1, b1, d1 `.steps`). */
const RolloutStepsComponent: React.FC<{ steps: PlanStepView[] }> = ({ steps }) => {
  const intl = useIntl();
  const { today, plan, actions, canAct } = useRollout();
  const facts = useMilestoneFacts();
  const rows = useMemo(() => rowsOf(steps, today), [steps, today]);
  const lineRows = rows.filter((row) => row.kind !== 'phase' && row.kind !== 'today' && row.kind !== 'skipped');
  const first = lineRows[0];
  const last = lineRows[lineRows.length - 1];

  const phaseSub = (phase: StepPhase): string => {
    const own = steps.filter((step) => step.phase === phase && step.day.skipped !== true);
    const done = own.filter((step) => step.tone === 'ok').length;
    if (done > 0) {
      return intl.formatMessage(rolloutMessages.phaseDone, { done, count: own.length });
    }
    if (phase === 'release') {
      return plan.releaseDate === null ? '' : planDayDate(intl, plan.releaseDate);
    }
    return intl.formatMessage(phase === 'build' ? rolloutMessages.phaseBuildSub : rolloutMessages.phaseAfterSub);
  };

  const renderRow = (row: Row): React.ReactElement => {
    const isFirst = row === first;
    const isLast = row === last;
    switch (row.kind) {
      case 'phase':
        return (
          <div key={`phase-${row.phase}`} className={styles.phase}>
            <h4>{intl.formatMessage(PHASE_TITLE[row.phase])}</h4>
            <small>{phaseSub(row.phase)}</small>
          </div>
        );
      case 'today':
        return (
          <div key="today" className={styles.todayRow}>
            <span>{intl.formatMessage(rolloutMessages.today)}</span>
            <span />
          </div>
        );
      case 'releaseDay':
        return <ReleaseDayMilestone key="releaseDay" isLast={isLast} />;
      case 'lyricVideo':
        return <LyricVideoMilestone key="lyricVideo" facts={facts} isLast={isLast} />;
      case 'canvas':
        return <CanvasMilestone key="canvas" facts={facts} isLast={isLast} />;
      case 'skipped':
        return (
          <div key={`skipped-${row.step.key}`} className={styles.skipped}>
            <FontAwesomeIcon icon={faXmark} />
            <span>{intl.formatMessage(rolloutMessages.skippedLine, { name: planDayTitle(intl, row.step.day) })}</span>
            <button
              type="button"
              className={styles.link}
              disabled={!canAct || actions.busy !== null}
              onClick={() => {
                actions.unskip('plan', row.step.key as RolloutDayKey).catch(console.error);
              }}
            >
              {intl.formatMessage(rolloutMessages.undoSkip)}
            </button>
          </div>
        );
      case 'step':
        return <StepCard key={`step-${row.step.key}`} step={row.step} isFirst={isFirst} isLast={isLast} />;
    }
  };

  return <div className={styles.steps}>{rows.map(renderRow)}</div>;
};

export const RolloutSteps = TypedMemo(RolloutStepsComponent);
